import { useMemo, useState } from 'react';

import { enqueueSnackbar } from 'notistack';

import type { MonthlyRecapsArticle, Region } from '@/features/MonthlyRecaps';

import type { FeedInfo } from '../types';

const DISMISSED_ARTICLES_KEY = 'monthly_recaps_dismissed_articles';
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

type DismissedArticle = {
  articleId: string;
  publishedAt: number;
};

function getArticleId(item: FeedInfo): string {
  return item.values.CTAValue;
}

function toDismissedArticle(item: FeedInfo): DismissedArticle {
  return {
    articleId: getArticleId(item),
    publishedAt: new Date(item.transactionTime).getTime()
  };
}

function makeRegionId(item: FeedInfo): string {
  return `${item.symbol.KeyCategory}_${item.symbol.KeyValue}`;
}

function isUnsuppressed(item: FeedInfo, dismissedArticles: DismissedArticle[]): boolean {
  const publishedAt = new Date(item.transactionTime).getTime();
  return dismissedArticles.every(
    dismissed => item.values.CTAValue !== dismissed.articleId || publishedAt > dismissed.publishedAt
  );
}

function isFresh(item: FeedInfo): boolean {
  return Date.now() - new Date(item.transactionTime).getTime() <= THIRTY_DAYS_MS;
}

function readDismissedArticles(): DismissedArticle[] {
  try {
    const stored = localStorage.getItem(DISMISSED_ARTICLES_KEY);
    if (!stored) return [];

    const dismissedArticles = JSON.parse(stored) as DismissedArticle[];
    return Array.isArray(dismissedArticles) ? dismissedArticles : [];
  } catch {
    return [];
  }
}

function persistDismissedArticles(dismissedArticles: DismissedArticle[]): void {
  try {
    localStorage.setItem(DISMISSED_ARTICLES_KEY, JSON.stringify(dismissedArticles));
  } catch {
    // ignore storage errors
  }
}

function mergeDismissedArticles(
  current: DismissedArticle[],
  next: DismissedArticle[]
): DismissedArticle[] {
  const byArticleId = new Map<string, DismissedArticle>();

  for (const dismissed of [...current, ...next]) {
    const existing = byArticleId.get(dismissed.articleId);
    if (!existing || dismissed.publishedAt > existing.publishedAt) {
      byArticleId.set(dismissed.articleId, dismissed);
    }
  }

  return [...byArticleId.values()].sort(
    (a, b) => b.publishedAt - a.publishedAt || a.articleId.localeCompare(b.articleId)
  );
}

function formatArticleMonth(isoDate: string): string {
  const date = new Date(isoDate);
  return `${date.toLocaleString('en-US', { month: 'long' })} ${date.getFullYear()} Recap`;
}

function formatArticleDate(isoDate: string): string {
  const date = new Date(isoDate);
  const day = date.getDate();
  const month = date.toLocaleString('en-US', { month: 'long' });
  return `${day} ${month} ${date.getFullYear()}`;
}

type MonthlyRecapsPromotionReturn = {
  show: boolean;
  regions: Region[];
  activeRegionId: string;
  article: MonthlyRecapsArticle | null;
  articleDialogArticle: MonthlyRecapsArticle | null;
  articleDialogCTAValue: string | null;
  setActiveRegionId: (id: string) => void;
  setArticleDialogCTAValue: (value: string | null) => void;
  dismiss: () => void;
  remindLater: () => void;
  readArticle: () => void;
  goBack: () => void;
  closeArticleDialog: () => void;
};

export function useMonthlyRecapsPromotion(
  data: FeedInfo[] | undefined
): MonthlyRecapsPromotionReturn {
  const [closedInSession, setClosedInSession] = useState(false);
  const [activeRegionIdState, setActiveRegionIdState] = useState('');
  const [articleDialogArticle, setArticleDialogArticle] = useState<MonthlyRecapsArticle | null>(
    null
  );
  const [articleDialogCTAValue, setArticleDialogCTAValue] = useState<string | null>(null);
  const [dismissedArticles, setDismissedArticles] =
    useState<DismissedArticle[]>(readDismissedArticles);

  const eligibleArticles = useMemo(
    () =>
      (data ?? [])
        .filter(item => isFresh(item))
        .filter(item => isUnsuppressed(item, dismissedArticles))
        .sort(
          (a, b) => new Date(b.transactionTime).getTime() - new Date(a.transactionTime).getTime()
        ),
    [data, dismissedArticles]
  );

  const show = eligibleArticles.length > 0 && !closedInSession;

  const regions: Region[] = useMemo(
    () =>
      eligibleArticles.map(item => ({
        id: makeRegionId(item),
        label: item.values.CTALabel || item.symbol.KeyValue
      })),
    [eligibleArticles]
  );

  const activeItem =
    eligibleArticles.find(item => makeRegionId(item) === activeRegionIdState) ??
    eligibleArticles.at(0) ??
    null;

  const activeRegionId = activeItem ? makeRegionId(activeItem) : '';

  const article: MonthlyRecapsArticle | null = activeItem
    ? {
        month: formatArticleMonth(activeItem.transactionTime),
        region: activeItem.values.CTALabel || activeItem.symbol.KeyValue,
        headline: activeItem.values.Title,
        description: activeItem.values.Content.replaceAll(/(^")|("$)/g, ''),
        date: formatArticleDate(activeItem.transactionTime),
        file: activeItem.values.CTAValue
      }
    : null;

  const suppressActiveArticle = () => {
    if (!activeItem) return;
    const dismissedArticlesToStore = mergeDismissedArticles(dismissedArticles, [
      toDismissedArticle(activeItem)
    ]);
    persistDismissedArticles(dismissedArticlesToStore);
    setDismissedArticles(dismissedArticlesToStore);
  };

  const suppressAvailableArticles = () => {
    const dismissedArticlesToStore = mergeDismissedArticles(
      dismissedArticles,
      eligibleArticles.map(toDismissedArticle)
    );
    persistDismissedArticles(dismissedArticlesToStore);
    setDismissedArticles(dismissedArticlesToStore);
  };

  const remindLater = () => {
    enqueueSnackbar("You'll find monthly summaries in your Feed anytime.", { variant: 'success' });
    setClosedInSession(true);
  };

  const dismiss = () => {
    enqueueSnackbar("You'll find monthly summaries in your Feed anytime.", { variant: 'success' });
    suppressAvailableArticles();
    setClosedInSession(true);
  };

  const readArticle = () => {
    if (article) {
      setArticleDialogArticle(article);
    }
    if (activeItem) {
      setArticleDialogCTAValue(activeItem.values.CTAValue);
    }
    setClosedInSession(true);
  };

  const goBack = () => {
    setArticleDialogArticle(null);
    setArticleDialogCTAValue(null);
    setClosedInSession(false);
  };

  const closeArticleDialog = () => {
    suppressActiveArticle();
    setArticleDialogArticle(null);
    setArticleDialogCTAValue(null);
  };

  return {
    show,
    regions,
    activeRegionId,
    article,
    articleDialogArticle,
    articleDialogCTAValue,
    setActiveRegionId: setActiveRegionIdState,
    setArticleDialogCTAValue,
    dismiss,
    remindLater,
    readArticle,
    goBack,
    closeArticleDialog
  };
}
